import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../composants/Button/Button';
import OperationCreationArticle from './OperationCreationArticle';
import Coordonnees from '../../../composants/Coordonnees/Coordonnees';
import CGVAccept from '../../../composants/CGVAccept/CGVAccept';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { StatusId, OperationMS } from '../types';
import { consultationNextTab } from '../domain/nextTab';
import {
  isConsultationEditable,
  validateConsultationForm,
  buildConsultationFormData,
  buildSaveBody,
  buildLancementPayload,
  mapApiErrorsToForm,
  CHAMP_TECHNIQUE_MAX,
  CHAMP_CAHIER_MAX,
} from '../domain/operationConsultation';
import { statusColorById, canAnnulerOperation } from '../domain/operationStatus';
import {
  saveOperationDescriptif,
  lancerConsultation,
  annulerOperation,
  ConsultationValidationError,
} from '../services/operationMsService';
import OperationDescriptifAdministratif from './OperationDescriptifAdministratif';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import OperationStatusBanner from './OperationStatusBanner';
import Banner from '../../../composants/Banner/Banner';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import { useDocuments } from '../../../contexts/DocumentsContext';
import TitulairesLotCards from '../../../composants/TitulairesLotCards/TitulairesLotCards';
import OperationProceduralRecap from './OperationProceduralRecap';
import OperationProlongationDelai from './OperationProlongationDelai';
import { scrollToTop } from '../../../utils/scroll';

const scrollToFirstError = (): void => {
  setTimeout(() => {
    const el = document.querySelector('.error, .error-message');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 100);
};

interface OperationConsultationProps {
  operation: OperationMS;
  setOperation: React.Dispatch<React.SetStateAction<OperationMS | null>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  refetch: () => void;
  printRef: React.RefObject<HTMLElement>;
}

// Onglet Consultation acheteur-MS (V2) : descriptif (admin + technique + financier + contact),
// sauvegarde et lancement de la consultation. Autonome, id-based, zero branche prestataire.
const OperationConsultation: React.FC<OperationConsultationProps> = ({
  operation,
  setOperation,
  errors,
  setErrors,
  refetch,
  printRef,
}) => {
  const navigate = useNavigate();
  const editable = isConsultationEditable(operation);
  // `editable` porte le bouton orange « Lancer la consultation » : il vaut bloqueur du CTA.
  const consultationCta = consultationNextTab(operation, editable);
  const formData = buildConsultationFormData(operation);

  // Pieces de l'accord-cadre en vigueur : documents communs (CCAP/CCTP...), deja charges par le
  // DocumentsProvider (monte dans App). Mappes au format attendu par DocumentsList (categorie commun
  // -> telechargement via /documents-communs/{id}/download).
  const { commonDocuments, loading: acDocsLoading } = useDocuments();
  const piecesAccordCadre = commonDocuments
    .filter((d) => d.actif)
    .map((d) => ({
      id: d.id,
      type: d.type,
      updated_at: d.updated_at,
      displayLabel: d.nom,
      category: 'commun' as const,
    }));

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [acceptCGV, setAcceptCGV] = useState(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToLaunchAfterCGV, setReadyToLaunchAfterCGV] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const hasArticles = useCallback(
    (): boolean =>
      (buildConsultationFormData(operation).sections || []).some((s) => (s.articles?.length ?? 0) > 0),
    [operation],
  );

  const patchFormulaire = useCallback(
    (patch: Record<string, unknown>) => {
      setOperation((prev) => (prev ? { ...prev, formulaire: { ...prev.formulaire, ...patch } } : prev));
    },
    [setOperation],
  );

  // Materialise le defaut "Public (P)" du niveau de sensibilite dans la SOURCE (operation.formulaire),
  // pas seulement dans l'affichage. Sans ca, le select montre "Public (P)" mais operation.formulaire
  // reste vide tant que l'utilisateur ne clique pas -> le lancement persiste NULL. Meme correctif que
  // la vue BC. La condition retombe a false au tour suivant : pas de boucle.
  useEffect(() => {
    if (editable && !operation.formulaire?.niveauSensibilite) {
      patchFormulaire({ niveauSensibilite: 'Public (P)' });
    }
  }, [editable, operation.formulaire?.niveauSensibilite, patchFormulaire]);

  const saveOperationData = useCallback(
    async (validateRequired: boolean): Promise<void> => {
      // Enregistrer et quitter -> AUCUNE validation (ni requis ni format), on sauvegarde tel quel.
      // Lancer la consultation -> validation complete (requis + format, le format venant du seed).
      if (validateRequired) {
        const v = validateConsultationForm(buildConsultationFormData(operation));
        if (!v.valid) {
          setErrors(v.errors);
          scrollToFirstError();
          throw new Error('Veuillez remplir tous les champs obligatoires');
        }
      }
      try {
        await saveOperationDescriptif(operation.id, buildSaveBody(operation));
        setErrors({});
      } catch (error) {
        if (error instanceof ConsultationValidationError) {
          setErrors(mapApiErrorsToForm(error.validationErrors));
          scrollToFirstError();
          throw new Error('Erreurs de validation détectées');
        }
        throw error;
      }
    },
    [operation, setErrors],
  );

  const confirmerLancement = useCallback(() => setIsConfirmModalOpen(true), []);

  const handleLancer = useCallback(async () => {
    if (!hasArticles()) {
      setErrorModal('Vous devez créer au moins un article avant de lancer la consultation.');
      return;
    }
    const v = validateConsultationForm(buildConsultationFormData(operation));
    if (!v.valid) {
      setErrors(v.errors);
      scrollToFirstError();
      return;
    }
    try {
      setIsSaving(true);
      await saveOperationData(true);
      setIsSaving(false);
      if (!acceptCGV) {
        setReadyToLaunchAfterCGV(true);
        setIsCGVModalOpen(true);
      } else {
        confirmerLancement();
      }
    } catch (error) {
      setIsSaving(false);
      const message = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';
      if (message !== 'Erreurs de validation détectées' && message !== 'Veuillez remplir tous les champs obligatoires') {
        setErrorModal('Erreur lors de la sauvegarde : ' + message);
      }
      scrollToFirstError();
    }
  }, [hasArticles, operation, setErrors, saveOperationData, acceptCGV, confirmerLancement]);

  // Apres lancement : refetch (statut -> 2) + bascule sur l'onglet Questions/reponses, sans reload.
  // Verrou anti double-submit : une opération déjà lancée (statut != brouillon) renverrait un 400
  // côté micro-service ; on empêche tout rejeu concurrent du lancement.
  const launchingRef = useRef(false);
  const executerLancement = useCallback(async () => {
    if (launchingRef.current) return;
    launchingRef.current = true;
    try {
      await lancerConsultation(operation.id, buildLancementPayload(operation));
      refetch();
      navigate({ search: '?tab=questions_reponses' });
      scrollToTop();
    } catch (error) {
      launchingRef.current = false; // échec : on autorise une nouvelle tentative
      const message = error instanceof Error ? error.message : 'Erreur lors du lancement de la consultation';
      setErrorModal(`Erreur : ${message}. Veuillez réessayer.`);
    }
  }, [operation, refetch, navigate]);

  const handleCancelOperation = useCallback(async () => {
    setIsDeleting(true);
    try {
      await annulerOperation(operation.id);
      navigate('/operations');
    } catch (error) {
      setErrorModal(error instanceof Error ? error.message : "Erreur lors de l'annulation de l'opération.");
    } finally {
      setIsDeleting(false);
      setIsCancelModalOpen(false);
    }
  }, [operation.id, navigate]);

  const handleSaveAndQuit = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveOperationData(false);
      navigate('/operations');
    } catch (error) {
      setErrorModal(error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.');
    } finally {
      setIsSaving(false);
    }
  }, [saveOperationData, navigate]);

  const annulerVisible = canAnnulerOperation(operation.status?.id);

  return (
    <div className="opms__consultation">
      {/* Descriptif Administratif (refonte PDF : Acheteur recap + Operation + Particularites) */}
      <SectionPliable title="Descriptif Administratif" defaultOpen>
        <div className="section-content">
          <OperationDescriptifAdministratif
            operation={operation}
            formData={formData}
            viewMode={!editable}
            errors={errors}
            setFormData={editable ? (newData) => patchFormulaire(newData) : undefined}
          />
        </div>
      </SectionPliable>

      {/* Descriptif Technique : attestation BPU + cahier des charges */}
      <SectionPliable title="Descriptif Technique" defaultOpen>
        <Banner
          variant="info"
          message="Les informations écrites ci-dessous sont contractuelles y compris les pièces éventuellement chargées au travers du lien URL. Mais, toutes précisions administratives ou 
            juridiques de nature à contrevenir à l'accord-cadre général sans dérogations prévues au contrat sont à considérer comme inexistantes dans ce descriptif"
        />
        <div className="section-content">
          {editable ? (
            <div className="form-group">
              <div className={`opms__attestation ${errors.attestationBPU ? 'opms__attestation--error' : ''}`}>
                <label className="opms__attestation-label">
                  <input
                    type="checkbox"
                    checked={!!formData.attestationBPU}
                    onChange={(e) => {
                      patchFormulaire({ attestationBPU: e.target.checked });
                      if (e.target.checked && errors.attestationBPU) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.attestationBPU;
                          return next;
                        });
                      }
                    }}
                  />
                  <span>
                    J'atteste que les BPU des bons de commande ne correspondent pas à mon besoin, et qu'il
                    est nécessaire de faire une consultation sur-mesure.<span style={{ color: 'red' }}> *</span>
                  </span>
                </label>
                {errors.attestationBPU && <div className="error-message">{errors.attestationBPU}</div>}
              </div>

              {/* Documentation technique (refonte xlsx, ligne 42) : 3 champs optionnels. */}
              <div className="opms__descriptif-admin opms__doc-technique">
                <div className="opms__admin-grid">
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>URL vers la doc technique et/ou planning détaillé</label>
                    <textarea
                      value={formData.urlDocTechnique || ''}
                      onChange={(e) => {
                        patchFormulaire({ urlDocTechnique: e.target.value });
                        if (errors.urlDocTechnique) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.urlDocTechnique;
                            return next;
                          });
                        }
                      }}
                      className={errors.urlDocTechnique ? 'error' : ''}
                      placeholder="ex : https://france-transfert-567890"
                      rows={3}
                      maxLength={CHAMP_TECHNIQUE_MAX}
                    />
                    <span className="opms__char-count">{(formData.urlDocTechnique || '').length}/{CHAMP_TECHNIQUE_MAX}</span>
                    {errors.urlDocTechnique && <span className="error-message">{errors.urlDocTechnique}</span>}
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>Modalité d'accès à la documentation</label>
                    <textarea
                      value={formData.modaliteAccesDocumentation || ''}
                      onChange={(e) => patchFormulaire({ modaliteAccesDocumentation: e.target.value })}
                      placeholder="ex : Nous contacter par mail pour obtenir le mot de passe"
                      rows={3}
                      maxLength={CHAMP_TECHNIQUE_MAX}
                    />
                    <span className="opms__char-count">{(formData.modaliteAccesDocumentation || '').length}/{CHAMP_TECHNIQUE_MAX}</span>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>Liste des pièces éventuelles composant la documentation technique</label>
                    <textarea
                      value={formData.listePiecesDocumentation || ''}
                      onChange={(e) => patchFormulaire({ listePiecesDocumentation: e.target.value })}
                      placeholder="ex : Planning détaillé, CCTP, plan du rez de chaussée, Coupe AA', Plan EP/EU, Charte spéciale BRUIT, etc…"
                      rows={3}
                      maxLength={CHAMP_TECHNIQUE_MAX}
                    />
                    <span className="opms__char-count">{(formData.listePiecesDocumentation || '').length}/{CHAMP_TECHNIQUE_MAX}</span>
                  </div>
                </div>
              </div>

              <label>
                Cahier des charges techniques et particularités à connaitre <span style={{ color: 'red' }}>*</span>
                {/* <span className="desc-precision">
                  (Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être
                  proposés par l'acheteur à titre d'information, mais seules les informations écrites ci-dessous
                  sont contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à
                  contrevenir à l'accord-cadre général sans dérogations prévues au contrat sont à considérer comme
                  inexistantes dans ce descriptif)
                </span> */}
              </label>
              <textarea
                value={formData.besoin || ''}
                onChange={(e) => {
                  patchFormulaire({ besoin: e.target.value });
                  if (e.target.value.trim() && errors.besoin) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.besoin;
                      return next;
                    });
                  }
                }}
                className={`definition-besoin-textarea ${errors.besoin ? 'error' : ''}`}
                placeholder="ex : les candidats sont informés qu'ils interviendront en site occupé et qu'ils ne pourront travailler que de 9h00 à 16h00. Les candidats ont pris connaissance de la ligne haute tension au droit du bâtiment et qu'il conviendra impérativement d'accéder par la partie sud pour acheminer la marchandise..."
                rows={20}
                maxLength={CHAMP_CAHIER_MAX}
              />
              <span className="opms__char-count">{(formData.besoin || '').length}/{CHAMP_CAHIER_MAX}</span>
              {errors.besoin && <span className="error-message">{errors.besoin}</span>}
            </div>
          ) : (
            <div className="form-group">
              {formData.attestationBPU && (
                <div className="opms__attestation-display">
                  Attestation : les BPU des bons de commande ne correspondent pas au besoin, une consultation
                  sur-mesure est nécessaire.
                </div>
              )}
              {/* Documentation technique (refonte xlsx) : recap lecture seule. */}
              <div className="opms__descriptif-admin opms__doc-technique">
                <div className="opms__admin-grid">
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>URL vers la doc technique et/ou planning détaillé</label>
                    <div className="input-inactif">{formData.urlDocTechnique || '-'}</div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>Modalité d'accès à la documentation</label>
                    <div className="input-inactif">{formData.modaliteAccesDocumentation || '-'}</div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>Liste des pièces éventuelles composant la documentation technique</label>
                    <div className="input-inactif">{formData.listePiecesDocumentation || '-'}</div>
                  </div>
                </div>
              </div>
              <label>Cahier des charges</label>
              <div className="input-inactif opms__besoin-readonly">{formData.besoin || '-'}</div>
            </div>
          )}
        </div>
      </SectionPliable>

      {/* Descriptif Financier */}
      <SectionPliable title="Descriptif Financier" defaultOpen>
        <Banner 
          variant="info" 
          message="Il vous appartient de créer la trame du DPGF (Détails des Prix Globaux et Forfaitaires) que vous souhaitez faire remplir par les prestataires. Au moins une ligne est 
          obligatoire pour pouvoir lancer la consultation. Facultatif : vous pouvez demander aux titulaires de vous faire passer un DPGF très détaillé parallèlement et ne construire ici que les chapitres et le total."
        />
        <div className="section-content">
          <OperationCreationArticle
            operationId={operation.id}
            formData={formData}
            viewMode={!editable}
            setFormData={editable ? (newData) => patchFormulaire(newData) : undefined}
          />
        </div>
      </SectionPliable>

      {/* Contacts */}
      {/* <SectionPliable title="Contacts" defaultOpen>
        <div className="section-content">
          <Coordonnees
            operation={operation}
            prestataire={undefined}
            isAcheteur
            isPresta={false}
            showSignataire={false}
            showDocuments={false}
            showTitulaires
            lotDepartementId={
              operation.lot_departement?.departement?.id !== undefined
                ? Number(operation.lot_departement.departement.id)
                : undefined
            }
            lotTechniqueId={operation.lot_technique?.id !== undefined ? Number(operation.lot_technique.id) : undefined}
          />
        </div>
      </SectionPliable> */}

      <SectionPliable title="Descriptif procédural" defaultOpen>
        <div className="section-content">
                <div className="no-print"><OperationStatusBanner operation={operation} context="consultation-brouillon" /></div>

          {/* Haut du descriptif procedural (refonte xlsx, lignes 57-64) : accord-cadre, lots, dates,
              budget/financement, sous-traitance, module Q/R, rappel des criteres. FRONT SEUL. */}
          <OperationProceduralRecap
            operation={operation}
            formData={formData}
            viewMode={!editable}
            errors={errors}
            setFormData={editable ? (newData) => patchFormulaire(newData) : undefined}
          />

          {/* Titulaires du lot qui sont consultes (refonte xlsx, ligne 65) : memes cartes que la
              modale "Lancer un marche subsequent" de la page Commandes. */}
          <div className="form-group" style={{ marginTop: '2rem' }}>
            <label>Titulaires du lot qui sont consultés (la notion de rang ne s’applique pas aux marchés subséquents)</label>
            <TitulairesLotCards
              lotTechnique={operation.id_lot_technique}
              departement={operation.lot_departement?.id_departement}
              mode="MS"
            />
          </div>

          {/* Documents liés à la consultation (refonte lignes 68-69) : pieces de l'accord-cadre (CCTP...)
              CLIQUABLES a gauche, et a droite le champ des pieces specifiques (miroir read-only du
              Descriptif technique, listePiecesDocumentation) qui occupe deux places de documents ->
              grille de 4 : CCTP1 | CCTP2 | champ (span 6). */}
          <div className="opms__descriptif-admin" style={{ marginTop: '2rem' }}>
            <div className="form-group">
              <label>Documents liés à la consultation <span style={{ color: 'red' }}>*</span></label>
              {/* Le champ des pieces specifiques (miroir read-only du Descriptif technique) est passe en
                  `trailingCard` a DocumentsList -> il devient une CARTE de la grille (meme ligne, meme
                  hauteur que les cartes CCTP, sans hack CSS externe). */}
              {acDocsLoading && piecesAccordCadre.length === 0 ? (
                <div className="input-inactif">Chargement des pièces…</div>
              ) : (
                <DocumentsList
                  documents={piecesAccordCadre}
                  variant="grid"
                  trailingCard={
                    <div className="opms__docs-consultation-champ">
                      <span className="opms__docs-consultation-champ-label">
                        Liste des pièces éventuelles composant la documentation technique
                      </span>
                      <div className="opms__docs-consultation-champ-inner">
                        {formData.listePiecesDocumentation || '-'}
                      </div>
                    </div>
                  }
                />
              )}
            </div>
          </div>
        </div>
        </SectionPliable>

      {/* Prolongation du délai de réception des offres : section verrouillée (cadenas) tant que la
          consultation n'est pas lancée, puis modification de la date + historique (date/heure + opérateur).
          Wrapper `opms__prolongation-section` : aligne le padding du corps sur les autres sections (4rem)
          sans modifier le composant accordéon partagé avec l'onglet Analyse. */}
      <div className="opms__prolongation-section">
        <OperationProlongationDelai operation={operation} onProlonged={refetch} />
      </div>


      {/* Rappel de la banniere d'etat juste avant les boutons (no-print : deja en tete de page). */}
      <div className="no-print"><OperationStatusBanner operation={operation} context="consultation" /></div>

      {/* Actions */}
      <div className="action-buttons opms__actions">
        
        {annulerVisible && (
          <Button
            value={isDeleting ? 'Annulation...' : 'Annuler la consultation'}
            variant="cancel"
            onClick={() => setIsCancelModalOpen(true)}
            disabled={isDeleting || isSaving}
          />
        )}

        <PrintButton
          targetRef={printRef}
          fileName={`${operation.numero ?? ''} - Consultation MS`}
          value="Imprimer la consultation"
          suiviHeaderProps={{
            titre: operation.nom || '',
            sousTitre: '',
            subtitle: `${operation.lot_technique?.id ?? ''} - ${operation.lot_technique?.nom ?? ''}`,
            status: operation.status?.nom || 'Statut inconnu',
            statusColor: statusColorById(operation.status?.id),
            delais: operation.expire_at,
          }}
        />

        {editable && (
          <>
            <Button
              value={isSaving ? 'Sauvegarde en cours...' : 'Sauvegarder et quitter'}
              variant="secondary"
              onClick={handleSaveAndQuit}
              disabled={isSaving}
            />
            <Button
              value="Lancer la consultation"
              variant="primary"
              onClick={handleLancer}
              disabled={isSaving}
              title={
                !acceptCGV
                  ? 'Vous devez accepter les CG'
                  : !hasArticles()
                    ? 'Vous devez créer au moins un article'
                    : ''
              }
            />
          </>
        )}

        {/* Consultation lancée : le bouton « Lancer » cede sa place au CTA vers l'onglet Q/R. */}
        <NextTabButton
          label={consultationCta.label}
          hidden={consultationCta.hidden}
          onClick={() => navigate({ search: `?tab=${consultationCta.target}` })}
        />
      </div>

      {/* Modales */}
      <CGVAccept
        acceptCGV={acceptCGV}
        setAcceptCGV={setAcceptCGV}
        isOpen={isCGVModalOpen}
        onClose={() => {
          setIsCGVModalOpen(false);
          setReadyToLaunchAfterCGV(false);
        }}
        onConfirm={() => {
          setAcceptCGV(true);
          setTimeout(() => {
            setIsCGVModalOpen(false);
            if (readyToLaunchAfterCGV) {
              setReadyToLaunchAfterCGV(false);
              confirmerLancement();
            }
          }, 100);
        }}
      />

      <ValidationPopUp
        isOpen={isConfirmModalOpen}
        title="Lancement de la consultation"
        text="Vous vous apprêtez à lancer la consultation. Les prestataires du lot seront sollicités. Cette action est irréversible."
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={async () => {
          setIsConfirmModalOpen(false);
          await executerLancement();
        }}
        cancelLabel="Annuler"
        confirmLabel="Confirmer"
      />

      <ValidationPopUp
        isOpen={isCancelModalOpen}
        title="Annulation de la consultation"
        text="Êtes-vous sûr de vouloir annuler cette consultation ? L'opération et les commandes liées passeront en statut annulé. Cette action est irréversible."
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelOperation}
        cancelLabel="Non"
        confirmLabel="Oui, annuler"
      />

      <ValidationPopUp
        isOpen={!!errorModal}
        title="Erreur"
        text={errorModal || ''}
        onClose={() => setErrorModal(null)}
        onConfirm={() => setErrorModal(null)}
        confirmLabel="Fermer"
      />
    </div>
  );
};

export default OperationConsultation;
